import type { Fraction } from '../../util/fractionType';

// MXF structural metadata definitions. Overview:
//
//  Preface
//    ContentStorage
//      MaterialPackage
//        Track1
//          Sequence
//            Component
//        Track2
//          Sequence
//            Component
//        TrackN
//          Sequence
//            Component
//      SourcePackage
//        Track1
//          Sequence
//            Component
//        Track2
//          Sequence
//            Component
//        TrackN
//          Sequence
//            Component
//
//
// The following interfaces define the unresolved MXF metadata structure
// as far as we care about it.

export interface Preface {
  type: 'preface',
  contentStorageRef : ContentStorage;
  identifications: Identification[];
  operationalPattern: string;
}

export interface ContentStorage {
  type: 'content_storage',
  packages: Package[];
}

// Vendor identification recording which tool wrote or modified
// the file.
export interface Identification {
  type: 'identification',
  companyName: string | null,
  productName: string | null,
  versionString: string | null,
}

export interface MaterialPackage {
  type: 'material_package',
  tracks: Track[];
  // Unix time in milliseconds (UTC)
  packageCreationDate: number | null;
  // Unix time in milliseconds (UTC)
  packageModifiedDate: number | null;
}

export interface SourcePackage {
  type: 'source_package',
  tracks: Track[];
  descriptor: MultipleDescriptor | SubDescriptor;
  // Unix time in milliseconds (UTC)
  packageCreationDate: number | null;
  // Unix time in milliseconds (UTC)
  packageModifiedDate: number | null;
}

export type Package = MaterialPackage | SourcePackage;

export interface TimelineTrack {
  type: 'timeline_track',
  editRate: Fraction;
  sequenceRef: Sequence;
  origin: number;
  /**
   *  TrackNumber is not actually the number of the track but
   *  rather a four byte sequence that matches the last four
   *  bytes of all essence KLVs of that track.
   */
  trackNumber: string,
  /**
   *  TrackId is closer to what you would normally call a track
   *  number however it does not always start at zero or one.
   */
  trackId: number,
}

export interface StaticTrack {
  type: 'static_track',
}

export type Track = TimelineTrack | StaticTrack;

export interface MultipleDescriptor {
  type: 'multiple_descriptor',
  subDescriptors: SubDescriptor[],
}

export interface Sequence {
  type: 'sequence',
  structuralComponents: Component[],
  duration: number | undefined,
}

export interface TimecodeComponent {
  type: 'timecode_component',
  duration: number | undefined,
  roundedTimecodeBase: number,
  startTimecode: number,
  dropFrame: boolean,
}

export interface SourceClip {
  type: 'source_clip',
  duration: number | undefined,
}

export type Component = SourceClip | TimecodeComponent;


export interface FileDescriptor {
  essenceContainer: string,
  linkedTrackId: number,
  containerDuration: number | undefined,
}

export interface GenericPictureEssenceDescriptor extends FileDescriptor {
  type: 'generic_picture_essence_descriptor',
  pictureEssenceCoding: string,
  storedWidth: number | null,
  storedHeight: number | null,
  sampledWidth: number | null,
  sampledHeight: number | null,
  displayWidth: number | null,
  displayHeight: number | null,
  // Display Aspect Ratio
  aspectRatio: Fraction | null,
  // SMPTE 377-1 FrameLayout enum:
  //  0= full_frame
  //  1= separate_fields
  //  2=single_field
  //  3= mixed_fields
  //  4=segmented_frame
  frameLayout: number | null,
  //  1 = field 1 first,
  //  2 = field 2
  fieldDominance: number | null,
  videoLineMap: number[] | null,
}

export interface GenericAudioEssenceDescriptor extends FileDescriptor {
  type: 'generic_audio_essence_descriptor',
  channelCount: number,
  quantizationBits: number,
  sampleRate: Fraction,
  audioSamplingRate: Fraction | null,
}

export interface UnknownEssenceDescriptor {
  type: 'unknown_essence_descriptor',
}

export type SubDescriptor =
  GenericPictureEssenceDescriptor |
  GenericAudioEssenceDescriptor |
  UnknownEssenceDescriptor;

export type Metadata =
  Preface |
  ContentStorage |
  Package |
  Track |
  Sequence |
  Component |
  MultipleDescriptor |
  SubDescriptor |
  Identification;

export function getMaterialPackage(contentStorage: ContentStorage): MaterialPackage {
  for (const p of contentStorage.packages) {
    if (p.type === 'material_package') {
      return p;
    }
  }
  throw new Error('No material package found');
}

export function getSourcePackage(contentStorage: ContentStorage): SourcePackage {
  for (const p of contentStorage.packages) {
    if (p.type === 'source_package') {
      return p;
    }
  }
  throw new Error('No source package found');
}

export function descriptorsForSourcePackage(sourcePackage: SourcePackage): SubDescriptor[] {
  if (sourcePackage.descriptor.type === 'multiple_descriptor') {
    return [...sourcePackage.descriptor.subDescriptors];
  } else {
    return [sourcePackage.descriptor];
  }
}
